import camelCase from 'lodash.camelcase';
import markerRed from '../../../../../assets/images/marker-red.svg';
import markerBlue from '../../../../../assets/images/marker-blue.svg';
import markerGreen from '../../../../../assets/images/marker-green.svg';
import markerYellow from '../../../../../assets/images/marker-yellow.svg';
import markerPurple from '../../../../../assets/images/marker-purple.svg';

// Preset pin colors available in the public report (mirrors hooks.js + ImageReportMap).
export const markerByColor = {
  red: markerRed,
  blue: markerBlue,
  green: markerGreen,
  yellow: markerYellow,
  purple: markerPurple,
};

// Vite resolves the imports above to asset URLs; default to red for unknown colors.
export const getMarkerByColor = (color) => markerByColor[color] || markerRed;

const asString = (value) => (typeof value === 'string' && value ? value : undefined);

// Marker metadata as exposed by ERS in the results snapshot. Answer-pin `inputs[].metadata` is
// camelCase (`pinSvg`); POI `mapLayers[].metadata` is the raw QBB blob (`pin_svg` snake_case) — so
// both spellings are read. Present only for the `custom_svg` pin type; otherwise `color` is the preset.
export const getCustomPinSvg = (metadata) =>
  asString(metadata?.pinSvg) ?? asString(metadata?.pin_svg);

// img-sourced SVG can't execute scripts; encodeURIComponent escapes #, quotes and newlines so the
// data-URI is valid. The markup is already DOMPurify-sanitized upstream in QBB — this is defense-in-depth.
export const svgToDataUri = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

// Custom pins are rasterized for Mapbox via `new Image()`. A standalone SVG sized in percentages (or
// with no width/height) rasterizes at the browser's 300x150 default and stretches a non-square icon
// into an ellipse. Pin the root <svg> to a fixed SQUARE box with `preserveAspectRatio="xMidYMid meet"`
// so it scales uniformly and centers. The preset markers are 40x40 and render at `icon-size: 1`, so
// match that footprint. Synthesize a viewBox from width/height when absent. Mapbox path only —
// Leaflet/inline-DOM markers are sized by their container.
export const CUSTOM_PIN_RASTER_SIZE = 40;

export const normalizeCustomPinSvg = (svg) =>
  svg.replace(/<svg\b[^>]*>/i, (openTag) => {
    const hasViewBox = /\sviewBox\s*=\s*"[^"]*"/i.test(openTag);
    const width = parseFloat(openTag.match(/\swidth\s*=\s*"([\d.]+)/i)?.[1] ?? '');
    const height = parseFloat(openTag.match(/\sheight\s*=\s*"([\d.]+)/i)?.[1] ?? '');

    let tag = openTag
      .replace(/\s(?:width|height)\s*=\s*"[^"]*"/gi, '')
      .replace(/\spreserveAspectRatio\s*=\s*"[^"]*"/gi, '');

    if (!hasViewBox && width > 0 && height > 0) {
      tag = tag.replace(/<svg\b/i, `<svg viewBox="0 0 ${width} ${height}"`);
    }

    return tag.replace(
      /<svg\b/i,
      `<svg width="${CUSTOM_PIN_RASTER_SIZE}" height="${CUSTOM_PIN_RASTER_SIZE}" preserveAspectRatio="xMidYMid meet"`
    );
  });

// Mapbox image id for a custom pin. Preset colors reuse the pre-registered `marker<Color>` images;
// custom pins register a per-entity image (lazily, via the styleimagemissing handler). The `kind`
// segment namespaces the id: ERS Input ids and QuestionMapLayer ids are independent sequences, so an
// answer pin and a POI on the same map can share a numeric id — the segment prevents a collision.
const CUSTOM_PIN_IMAGE_PREFIX = 'customPin-';
export const customPinImageId = (id, kind = 'input') => `${CUSTOM_PIN_IMAGE_PREFIX}${kind}-${id}`;
export const isCustomPinImageId = (id) => id.startsWith(CUSTOM_PIN_IMAGE_PREFIX);

// The Mapbox `icon-image` for an answer pin: a per-input custom image id when custom, else the
// pre-registered preset image (`markerRed`, `markerBlue`, ...).
export const iconImageForInput = (metadata, id) =>
  getCustomPinSvg(metadata)
    ? customPinImageId(id, 'input')
    : camelCase(`marker-${asString(metadata?.color) ?? 'red'}`);

// The Mapbox `icon-image` for a POI map layer: a per-layer custom image id when the layer carries a
// custom SVG, else the shared `markerPoi` preset.
export const iconImageForMapLayer = (metadata, id) =>
  getCustomPinSvg(metadata) ? customPinImageId(id, 'mapLayer') : 'markerPoi';
